import API from '../endpoints';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Article, ArticleOrder, ArticleSort, ArticleStatus, PaginatedResponse, TagsMatchMode } from '@repo/types';
import { getAccessToken } from '../utils/getAccessToken';
import { useGlobalContext } from '@slc/tools/GlobalContext';

// 1 hour frontend cache for article list query
const CACHE_TIME = 60 * 60 * 1000;

export type ArticlesQueryArgs = {
  tags?: string;
  tagsMatchMode?: TagsMatchMode;
  startDate?: string;
  endDate?: string;
  limit?: number;
  page?: number;
  sort?: ArticleSort;
  order?: ArticleOrder;
  shared?: boolean;
  ignorePublishDate?: boolean;
  excludeIds?: string;
  excludeTags?: string;
  titleQuery?: string;
  query?: string;
  // Comma-separated division tags / club slugs, or the literal "all".
  // The API resolves both (expanding "all") and matches them as tags.
  division?: string;
  club?: string;
  // Drafts or published only. The API honours it for admin callers only.
  status?: ArticleStatus;
};

type QueryOptions = {
  enabled: boolean;
};

export const useArticlesQuery = (args: ArticlesQueryArgs, options?: QueryOptions) => {
  const { apiUrl } = useGlobalContext();

  return useQuery({
    queryKey: ['articles', args, apiUrl],
    queryFn: () => fetchArticles(apiUrl, args),
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME,
    enabled: !!apiUrl && (options?.enabled ?? true),
  });
};

const fetchArticles = async (apiUrl: string, params: ArticlesQueryArgs): Promise<PaginatedResponse<Article>> => {
  const accessToken = getAccessToken();
  const { data } = await axios.get<PaginatedResponse<Article>>(`${apiUrl}${API.endpoints.articles}`, {
    params,
    headers: accessToken
      ? {
          Authorization: `Bearer ${accessToken}`,
        }
      : {},
  });
  return data;
};

export type InfiniteArticlesQueryArgs = Omit<ArticlesQueryArgs, 'page'>;

export const useInfiniteArticlesQuery = (args: InfiniteArticlesQueryArgs, options?: QueryOptions) => {
  const { apiUrl } = useGlobalContext();

  return useInfiniteQuery({
    queryKey: ['articles-infinite', args, apiUrl],
    queryFn: async ({ pageParam = 1 }) => {
      const accessToken = getAccessToken();
      const { data } = await axios.get<PaginatedResponse<Article>>(`${apiUrl}${API.endpoints.articles}`, {
        params: { ...args, page: pageParam },
        headers: accessToken
          ? {
              Authorization: `Bearer ${accessToken}`,
            }
          : {},
      });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.hasNextPage) {
        return lastPage.pagination.currentPage + 1;
      }
      return undefined;
    },
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME,
    enabled: !!apiUrl && (options?.enabled ?? true),
  });
};
